import { useParams } from 'react-router';
import { useMutation } from '@apollo/client';

import { apolloClientContracts } from '@/apollo/clients/contracts.apollo';
import { ALL_ITEMS } from '@/config/common.config';
import { ApproveContractLines, ApproveContractLinesVariables }
  from '@/graphql/contracts/_generated_/approveContractLines.mutation';
import ApproveContractLinesMutation from '@/graphql/contracts/approveContractLines.mutation.gql';
import GetContractDetailsQuery from '@/graphql/contracts/getContractDetails.query.gql';
import GetPaginatedFilteredVariableContractLinesQuery from
  '@/graphql/contracts/getPaginatedFilteredVariableContractLines.query.gql';
import { notificationService } from '@/services/notification/notification.client';
import GetBCContractLinesQuery from '@/graphql/contracts/getPaginatedFilteredBCContractLines.query.gql';
import { triggerLineRefresh, useSelectedLines } from
  '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/linesSelectionObservable';
import { triggerVariableRefresh } from
  '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/variableLinesSelectionObservable';

export type ApproveContractLinesInput = Pick<ApproveContractLinesVariables, 'contractLines' | 'comment'>;

export default function useApproveContractLines() {
  const { contractId }: { contractId: UUID } = useParams();
  const selectedLines = useSelectedLines();

  const [_approveContractLines, { loading, error }] = useMutation<ApproveContractLines, ApproveContractLinesVariables>(
    ApproveContractLinesMutation,
    {
      client: apolloClientContracts,
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetContractDetailsQuery,
          variables: { contractId }
        },
        selectedLines.length ?
          {
            query: GetBCContractLinesQuery,
            variables: {
              filter: {
                contractId: { eq: contractId }
              },
              first: ALL_ITEMS
            }
          } :
          {
            query: GetPaginatedFilteredVariableContractLinesQuery,
            variables: {
              filter: { headerPrimaryKey: { eq: contractId } },
              first: ALL_ITEMS,
            }
          }
      ],
      onError: (err) => notificationService.open({
        title: 'Error',
        text: err.message,
        type: 'danger'
      }),
    }
  );

  const approveContractLines = (input: ApproveContractLinesInput, callback?: () => void) => {
    _approveContractLines({
      variables: {
        contractId,
        ...input
      },
      onCompleted: () => {
        triggerLineRefresh();
        triggerVariableRefresh();
        if (typeof callback === 'function') {
          callback();
        }

        notificationService.open({
          title: 'Contract Line Status Saved',
          text: 'The chosen contract lines have been changed',
          type: 'success'
        });
      }
    });
  };

  return {
    approveContractLines,
    isLoadingApprove: loading,
    errorApprove: error,
  } as const;
}
